import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Plane } from "lucide-react";
import { toast } from "sonner";
import { Header } from "../components/Layout";
import { AirlineLogo } from "../components/FlightCard";
import { TicketDownload } from "../components/TicketFile";
import { useAuth } from "../context/AuthContext";
import { http, money, dateLabel, time, errMsg, login, StatusBadge } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function Trips() {
  const { user, loading, config } = useAuth();
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("active");
  const load = () => http.get("/bookings").then((r) => setList(r.data));
  useEffect(() => { if (user) load(); }, [user]);

  const cancel = async (id) => {
    try {
      const { data } = await http.post(`/bookings/${id}/cancel`);
      toast.success(data.refund_amount ? `Cancelled. Refund of ${money(data.refund_amount)} initiated` : "Booking cancelled");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const requestCancel = async (id) => {
    try {
      const { data } = await http.post(`/bookings/${id}/request-cancel`);
      toast.success(`Cancellation requested. Estimated refund ${money(data.estimated_refund)}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = (list || []).filter((b) => (tab === "cancelled" ? b.status === "cancelled" : b.status !== "cancelled"));

  return (
    <div className="min-h-screen bg-[#F4F6F9] pb-16">
      <Header />
      <div className="mx-auto max-w-4xl px-4 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-3"><Briefcase className="h-8 w-8 text-[#EC5B24]" /> My Trips</h1>
        {!loading && !user && (
          <div className="bg-white rounded-2xl p-10 text-center mt-6" data-testid="trips-login-prompt">
            <p className="text-slate-600">Log in to see your bookings</p>
            <button onClick={login} data-testid="trips-login-btn" className="mt-4 rounded-full bg-[#EC5B24] text-white font-semibold px-6 py-2.5">Log in</button>
          </div>
        )}
        {user && (
          <>
            <div className="flex gap-2 mt-6 mb-4">
              {[["active", "My bookings"], ["cancelled", "Cancelled"]].map(([id, l]) => (
                <button key={id} data-testid={`trips-tab-${id}`} onClick={() => setTab(id)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold ${tab === id ? "bg-[#1C1A4A] text-white" : "bg-white text-slate-600"}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-3">
              {shown.map((b) => {
                const s = b.offer.slices[0];
                return (
                  <div key={b.booking_id} className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`trip-card-${b.booking_id}`}>
                    <AirlineLogo code={s.airline_code} logo={s.airline_logo} size="h-11 w-11" />
                    <div className="flex-1">
                      <div className="font-heading font-bold text-lg flex flex-wrap items-center gap-2">{s.origin} → {s.destination}{b.offer.slices.length > 1 ? " (round trip)" : ""} <StatusBadge status={b.status} /></div>
                      {b.offer.slices.map((x, i) => (
                        <div key={i} className="text-sm text-slate-500" data-testid={`trip-leg-${b.booking_id}-${i}`}>{b.offer.slices.length > 1 ? (i ? "Return: " : "Departure: ") : ""}{dateLabel(x.depart_at)} · {time(x.depart_at)} · {x.flight_no}</div>
                      ))}
                      {b.pnr && <div className="text-sm text-slate-500">PNR <span className="font-mono font-semibold">{b.pnr}</span></div>}
                      <div className="text-xs text-slate-400 mt-0.5">{b.travellers.length} traveller(s) · {money(b.prices.total, b.currency)}{b.offer.slices.length > 1 ? " for both flights" : ""}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {["pending_payment", "payment_rejected"].includes(b.status) && (
                        <Link to={`/pay/${b.booking_id}`} data-testid={`pay-trip-${b.booking_id}`} className="rounded-full bg-[#EC5B24] text-white px-4 py-2 text-sm font-semibold">Pay now</Link>
                      )}
                      {["payment_submitted", "payment_verified", "ticketed"].includes(b.status) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><button data-testid={`request-cancel-${b.booking_id}`} className="rounded-full border border-red-200 text-red-500 px-4 py-2 text-sm font-semibold">Cancel</button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Request cancellation?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {b.assured ? <>You have Full refund protection. Refund: <b>{money(b.prices.total - (b.prices.assured || 0))}</b> (everything except the protection fee).</> : <>
                                Cancellation charge is {money((b.cancel_fee_per_person ?? config.cancel_fee ?? 900) * b.travellers.length)} ({money(b.cancel_fee_per_person ?? config.cancel_fee ?? 900)} × {b.travellers.length}).
                                Estimated refund: <b>{money(Math.max(b.prices.total - (b.cancel_fee_per_person ?? config.cancel_fee ?? 900) * b.travellers.length, 0))}</b>, less any airline fees as per fare rules.</>}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep booking</AlertDialogCancel>
                              <AlertDialogAction data-testid="confirm-request-cancel-btn" onClick={() => requestCancel(b.booking_id)} className="bg-red-500 hover:bg-red-600">Request cancellation</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <TicketDownload booking={b} />
                      <Link to={`/booking/${b.booking_id}`} data-testid={`view-trip-${b.booking_id}`} className="rounded-full border px-4 py-2 text-sm font-semibold">View</Link>
                      {(b.status === "confirmed" || ["pending_payment", "payment_rejected"].includes(b.status)) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><button data-testid={`cancel-trip-${b.booking_id}`} className="rounded-full border border-red-200 text-red-500 px-4 py-2 text-sm font-semibold">Cancel</button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
                              <AlertDialogDescription>{b.status !== "confirmed" ? "This unpaid booking will be cancelled." : b.assured || b.offer.refundable ? "You will get a full refund." : "Airline cancellation charges of ₹3,500 apply."}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep booking</AlertDialogCancel>
                              <AlertDialogAction data-testid="confirm-cancel-btn" onClick={() => cancel(b.booking_id)} className="bg-red-500 hover:bg-red-600">Yes, cancel</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                );
              })}
              {list && !shown.length && (
                <div className="bg-white rounded-2xl p-12 text-center" data-testid="trips-empty">
                  <Plane className="h-10 w-10 mx-auto text-slate-300" />
                  <p className="text-slate-500 mt-3">No {tab === "active" ? "active" : "cancelled"} trips</p>
                  <Link to="/" className="inline-block mt-4 text-[#EC5B24] font-semibold">Book a flight</Link>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
